import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../css/models-common.css";
import "../../css/chart-main.css";
import "../../css/models-overview.css";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { useProductOverview } from "../../hooks";
import { productService } from "../../services";
import EmptyStateCard from "../../component/EmptyStateCard";
import { translateSectorLabel } from "../../utils/sectorLabel";
import MiniChartSVG from "../../component/models/MiniChartSVG";
import ModelBuilderOverlay from "../../component/models/builder/ModelBuilderOverlay";
import { ToastContainer, useToast } from "../../component/Toast";
import { transformModelDataToApiFormat, hasNestedSubProducts } from "../../utils/modelTransforms";

const ModelsOverview = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;

  // Toast notifications
  const { toasts, addToast, removeToast } = useToast();

  // Model Builder overlay state
  const [showModelBuilder, setShowModelBuilder] = useState(false);
  const [editingModelId, setEditingModelId] = useState(null);

  // Fetch product overview data from API
  const { data: overview, loading, error, refetch } = useProductOverview({
    limitRecent: 8,
    limitModels: 30,
  });

  const isServerError = (err) => {
    return err && (err.message?.includes('502') || err.message?.includes('Bad Gateway'));
  };

  const serverError = isServerError(error);

  // Extract data from overview response
  const recentModels = overview?.recent_views || [];
  const yourModels = overview?.your_models || [];
  const demoModels = overview?.demo_models || [];
  const relatedFields = overview?.related_fields || [];
  const allSectors = overview?.all_sectors || [];

  const handleModelClick = (modelId) => {
    navigate(`/models/model/${modelId}`);
  };

  const handleSectorClick = (sectorId, sectorName) => {
    // Navigate to models sector page
    navigate(`/models/sector/${sectorId}`, { state: { sectorName } });
  };

  const handleCreateModel = () => {
    setEditingModelId(null);
    setShowModelBuilder(true);
  };

  const handleModelSave = async (modelData) => {
    try {
      // Transform UI data to API format
      const apiPayload = transformModelDataToApiFormat(modelData, 'New Model');

      let createdProduct;

      // Use nested endpoint if model has sub-products with nested components
      if (hasNestedSubProducts(modelData)) {
        createdProduct = await productService.createNested(apiPayload);
      } else {
        createdProduct = await productService.create(apiPayload);
      }

      // Upload image if provided
      if (modelData.imageFile && createdProduct?.id) {
        try {
          const formData = new FormData();
          formData.append('image_file', modelData.imageFile);
          await productService.uploadImage(createdProduct.id, formData);
        } catch (imageError) {
          console.warn('Image upload failed, product created without image:', imageError);
          // Don't fail the whole operation if image upload fails
        }
      }

      // Close the builder and refresh the overview data
      setShowModelBuilder(false);
      addToast(t('models.overview.createSuccess'), 'success');
      // Invalidate cache and refetch fresh data
      refetch();
    } catch (error) {
      console.error('Error saving model:', error);
      addToast(`${t('models.overview.createError')}: ${error.message || ''}`, 'error', 6000);
    }
  };

  return (
    <div className="models-overview">
      {/* Header with Create Button */}
      <div className="models-overview-header">
        <button className="btn-create-model" onClick={handleCreateModel}>
          <span className="plus-icon">+</span>
          {t('models.overview.createNew')}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <p>{t('models.overview.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {serverError && (
        <div className="error-state">
          <p>Data is temporarily unavailable. Please try again later.</p>
        </div>
      )}

      {error && !serverError && (
        <div className="error-state">
          <p>Error loading data: {error.message}</p>
        </div>
      )}

      {/* Content - only show when not loading and no error */}
      {!loading && !error && (
        <>
          {/* Recent Views Section - Only show if there are recent views */}
          {recentModels.length > 0 && (
            <section className="overview-section">
              <h2 className="section-title">{t('models.overview.recentViews')}</h2>
              <div className="materials-grid">
                {recentModels.map((model) => (
                  <div
                    key={model.id}
                    className="material-card"
                    onClick={() => handleModelClick(model.id)}
                  >
                    {/* Price and Date - Top Right */}
                    <div className="material-header">
                      <div className="material-price">{model.price}</div>
                      <div className="material-date">{model.date}</div>
                    </div>

                    {/* Chart - Center, Full Width */}
                    <div className="material-chart-wrapper">
                      <MiniChartSVG data={model.chartData} uniqueId={model.id} color="#EB5757" />
                    </div>

                    {/* Material Name - Bottom Left */}
                    <div className="material-footer">
                      <div className="material-name">{model.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Your Models Section */}
          <section className="overview-section">
            <h2 className="section-title">{t('models.overview.yourModels')}</h2>
            {yourModels.length === 0 ? (
              <EmptyStateCard
                title={t('models.overview.noModels')}
                subtitle={t('models.overview.noModelsHint')}
              />
            ) : (
              <div className="materials-grid">
                {yourModels.map((model) => (
                  <div
                    key={model.id}
                    className="material-card"
                    onClick={() => handleModelClick(model.id)}
                  >
                    {/* Price and Date - Top Right */}
                    <div className="material-header">
                      <div className="material-price">{model.price}</div>
                      <div className="material-date">{model.date}</div>
                    </div>

                    {/* Chart - Center, Full Width */}
                    <div className="material-chart-wrapper">
                      <MiniChartSVG data={model.chartData} uniqueId={model.id} color="#EB5757" />
                    </div>

                    {/* Material Name - Bottom Left */}
                    <div className="material-footer">
                      <div className="material-name">{model.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Demo Models Section - Only show if there are demo models */}
          {demoModels.length > 0 && (
            <section className="overview-section">
              <h2 className="section-title">{t('models.overview.demoModels')}</h2>
              <div className="materials-grid">
                {demoModels.map((model) => (
                  <div
                    key={`demo-${model.id}`}
                    className="material-card"
                    onClick={() => handleModelClick(model.id)}
                  >
                    {/* Price and Date - Top Right */}
                    <div className="material-header">
                      <div className="material-price">{model.price}</div>
                      <div className="material-date">{model.date}</div>
                    </div>

                    {/* Chart - Center, Full Width */}
                    <div className="material-chart-wrapper">
                      <MiniChartSVG data={model.chartData} uniqueId={model.id} color="#EB5757" />
                    </div>

                    {/* Material Name - Bottom Left */}
                    <div className="material-footer">
                      <div className="material-name">{model.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Related Fields Section - From API */}
          {relatedFields.length > 0 && (
            <section className="overview-section">
              <h2 className="section-title">{t('models.overview.relatedFields')}</h2>
              <div className="sectors-grid">
                {relatedFields.map((sector) => (
                  <div
                    key={`related-${sector.id}`}
                    className="sector-card"
                    onClick={() => handleSectorClick(sector.id, sector.name)}
                  >
                    <div className="sector-image">
                      <img
                        src={sector.image_url || placeholderImage}
                        alt={translateSectorLabel(sector.name, lang)}
                        onError={(e) => { e.target.src = placeholderImage; }}
                      />
                      <div className="sector-name-overlay">{translateSectorLabel(sector.name, lang)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* All Sectors Section */}
          <section className="overview-section">
            <h2 className="section-title">{t('models.overview.allSectors')}</h2>
            {allSectors.length === 0 ? (
              <div className="u-empty-state">
                <p>{t('models.overview.noSectors')}</p>
              </div>
            ) : (
              <div className="sectors-grid">
                {allSectors.map((sector) => (
                  <div
                    key={`all-${sector.id}`}
                    className="sector-card"
                    onClick={() => handleSectorClick(sector.id, sector.name)}
                  >
                    <div className="sector-image">
                      <img
                        src={sector.image_url || placeholderImage}
                        alt={translateSectorLabel(sector.name, lang)}
                        onError={(e) => { e.target.src = placeholderImage; }}
                      />
                      <div className="sector-name-overlay">{translateSectorLabel(sector.name, lang)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Model Builder Overlay */}
      <ModelBuilderOverlay
        isOpen={showModelBuilder}
        onClose={() => setShowModelBuilder(false)}
        modelId={editingModelId}
        onSave={handleModelSave}
      />
    </div>
  );
};

export default ModelsOverview;
